import React from 'react';

export type ToastStyle = 'none' | 'success' | 'error' | 'warning' | 'info';

interface ToastContext {
  onToastClose: () => void;
  onToastOpen: ({
    style,
    body,
    title
  }: {
    title?: string | undefined;
    body?: React.ReactNode;
    style: ToastStyle;
  }) => void;
}

const ToastContext = React.createContext<ToastContext>({
  onToastClose: () => {},
  onToastOpen: () => {}
});

export const ToastProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [state, setState] = React.useState<{
    show: boolean;
    title?: string;
    body?: React.ReactNode;
    style: ToastStyle;
    timeout?: number;
  }>({
    show: false,
    style: 'none'
  });

  const onToastClose = () => setState((prev) => ({ ...prev, show: false }));
  const onToastOpen = ({
    style,
    body,
    title
  }: {
    title?: string;
    body?: React.ReactNode;
    style: ToastStyle;
  }) =>
    setState({
      show: true,
      style,
      ...(title !== undefined && {
        title
      }),
      ...(body !== undefined && {
        body
      })
    });

  const getTitleStyle = () => {
    switch (state.style) {
      case 'error':
        return 'text-danger';
      case 'info':
        return 'text-info';
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      default:
      case 'none':
        return undefined;
    }
  };

  React.useEffect(() => {
    if (!state.show) return;

    setTimeout(() => {
      onToastClose();
    }, 5000);
  }, [state.show]);

  return (
    <ToastContext.Provider value={{ onToastOpen, onToastClose }}>
      {children}

      <div
        className="bottom-0 end-0 p-3"
        style={{
          position: 'fixed',
          zIndex: 999999
        }}>
        <div
          id="liveToast"
          className={['toast', state.show ? 'show' : 'hide'].join(' ')}
          role="alert"
          aria-live="assertive"
          aria-atomic="true">
          {state.title && (
            <div className="toast-header">
              <strong
                className={[
                  'me-auto',
                  getTitleStyle() ? getTitleStyle() : ''
                ].join(' ')}>
                {state.title}
              </strong>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="toast"
                aria-label="Close"
                onClick={onToastClose}></button>
            </div>
          )}

          <div className="toast-body">{state.body}</div>
        </div>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  return React.useContext(ToastContext);
};
